import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  CalendarDays,
  ClipboardCheck,
  Link2,
  LogOut,
  Plus,
  Users,
  MessageSquare,
  Building2,
  Briefcase,
  Settings as SettingsIcon,
} from "lucide-react";
import { toast } from "sonner";
import PortalInbox from "../components/PortalInbox";

const PortalHomePage = () => {
  const [me, setMe] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [marketplacePosts, setMarketplacePosts] = useState([]);
  const [myMarketplaceInterests, setMyMarketplaceInterests] = useState([]);
  const [marketplaceFilters, setMarketplaceFilters] = useState({ search: "", trade: "", state: "" });
  const [interestForms, setInterestForms] = useState({});
  const [availability, setAvailability] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("all");
  const [form, setForm] = useState({ status: "available", start_date: "", end_date: "", notes: "" });
  const [settingsForm, setSettingsForm] = useState({ full_name: "", phone: "", password: "" });

  const token = localStorage.getItem("portalToken");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = async () => {
    try {
      const tenantParam = selectedTenantId !== "all" ? { tenant_id: selectedTenantId } : {};
      const marketplaceParams = Object.fromEntries(Object.entries(marketplaceFilters).filter(([, value]) => value));
      const [meRes, bookingsRes, marketplaceRes, interestsRes, availabilityRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/portal/me`, { headers }),
        axios.get(`${API}/portal/bookings`, { headers, params: tenantParam }),
        axios.get(`${API}/portal/marketplace/opportunities`, { headers, params: marketplaceParams }),
        axios.get(`${API}/portal/marketplace/interests`, { headers }),
        axios.get(`${API}/portal/availability`, { headers, params: tenantParam }),
        axios.get(`${API}/portal/assignments`, { headers, params: tenantParam }),
      ]);
      setMe(meRes.data);
      setBookings(bookingsRes.data);
      setMarketplacePosts(marketplaceRes.data || []);
      setMyMarketplaceInterests(interestsRes.data || []);
      setAvailability(availabilityRes.data);
      setAssignments(assignmentsRes.data);
      setSettingsForm((current) => ({
        ...current,
        full_name: meRes.data?.worker?.full_name || "",
        phone: meRes.data?.worker?.phone || "",
      }));
    } catch {
      toast.error("Failed to load worker portal");
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token, selectedTenantId]);

  const handleLogout = () => {
    localStorage.removeItem("portalToken");
    window.location.href = "/portal/login";
  };

  const handleAvailabilitySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/portal/availability`, form, { headers });
      setForm({ status: "available", start_date: "", end_date: "", notes: "" });
      await fetchData();
      toast.success("Availability updated");
    } catch {
      toast.error("Failed to update availability");
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/portal/me`, settingsForm, { headers });
      setSettingsForm((current) => ({ ...current, password: "" }));
      await fetchData();
      toast.success("Settings updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    }
  };

  const updateBooking = async (bookingId, status) => {
    try {
      await axios.put(`${API}/portal/bookings/${bookingId}`, { status }, { headers });
      await fetchData();
      toast.success(`Booking ${status}`);
    } catch {
      toast.error("Failed to update booking");
    }
  };

  const submitMarketplaceInterest = async (postId) => {
    try {
      const form = interestForms[postId] || {};
      await axios.post(`${API}/portal/marketplace/opportunities/${postId}/interest`, form, { headers });
      setInterestForms((current) => ({ ...current, [postId]: { message: "", availability_start: "", availability_end: "", proposed_amount: "" } }));
      await fetchData();
      toast.success("Interest submitted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit interest");
    }
  };

  const withdrawMarketplaceInterest = async (interestId) => {
    try {
      await axios.post(`${API}/portal/marketplace/interests/${interestId}/withdraw`, {}, { headers });
      await fetchData();
      toast.success("Interest withdrawn");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to withdraw interest");
    }
  };

  const memberships = me?.memberships || [];
  const committedProjects = assignments.filter((item) => item.status && !["cancelled", "planned"].includes(item.status));
  const opportunities = bookings.filter((item) => ["pending", "countered"].includes(item.status));
  const openMarketplaceCount = marketplacePosts.filter((item) => !item.my_interest || item.my_interest.status === "withdrawn").length;

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Worker Portal</h1>
          <p className="text-muted-foreground">{me?.worker?.full_name}</p>
          {memberships.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">Connected partner relationships: {memberships.length}</p>
          )}
        </div>
        <Button variant="outline" onClick={handleLogout}><LogOut className="mr-2 h-4 w-4" /> Sign out</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card><CardContent className="p-5"><div className="text-2xl font-bold">{memberships.length || 0}</div><div className="text-sm text-muted-foreground">Partners</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-2xl font-bold">{openMarketplaceCount}</div><div className="text-sm text-muted-foreground">Open Marketplace</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-2xl font-bold">{committedProjects.length}</div><div className="text-sm text-muted-foreground">Committed Projects</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-2xl font-bold">{availability.length}</div><div className="text-sm text-muted-foreground">Availability Blocks</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-2xl font-bold">{me?.message_count || 0}</div><div className="text-sm text-muted-foreground">Messages</div></CardContent></Card>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 rounded-xl border border-border/60 bg-card p-4">
        <div className="min-w-[240px] space-y-2">
          <Label>View partner context</Label>
          <select
            className="w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
          >
            <option value="all">All partner relationships</option>
            {memberships.map((membership) => (
              <option key={membership.membership_id} value={membership.tenant_id}>
                {(membership.tenant_name || membership.tenant_id)} {membership.company_name ? `• ${membership.company_name}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="text-sm text-muted-foreground">
          Switch context to focus on one partner relationship or view everything together.
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-7 h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger>
          <TabsTrigger value="partners">Partners</TabsTrigger>
          <TabsTrigger value="marketplace">Open Marketplace</TabsTrigger>
          <TabsTrigger value="projects">Committed Projects</TabsTrigger>
          <TabsTrigger value="opportunities">Direct Requests</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="space-y-6 xl:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Quick Partner Snapshot</CardTitle>
                  <CardDescription>Your current active working relationships.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {memberships.length > 0 ? memberships.slice(0, 4).map((membership) => (
                    <div key={membership.membership_id} className="border rounded-xl p-3 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{membership.company_name || "Unknown company"}</div>
                          <div className="text-sm text-muted-foreground">{membership.tenant_name || membership.tenant_id}</div>
                        </div>
                        <Badge variant="outline" className="capitalize">{membership.membership_status}</Badge>
                      </div>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No active partner connections yet.</p>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4" /> Add Availability</CardTitle>
                  <CardDescription>Availability is shared across your active tenant relationships.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAvailabilitySubmit} className="space-y-4">
                    <div className="space-y-2"><Label>Status</Label><Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="bg-muted/50" /></div>
                    <div className="space-y-2"><Label>Start</Label><Input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-muted/50" /></div>
                    <div className="space-y-2"><Label>End</Label><Input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-muted/50" /></div>
                    <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-muted/50" /></div>
                    <Button type="submit" className="w-full bg-primary hover:bg-primary/90">Save Availability</Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="xl:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Direct Requests</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {opportunities.length > 0 ? opportunities.map((booking) => (
                    <div key={booking.id} className="border rounded-xl p-4 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-semibold">{booking.title}</div>
                          <div className="text-sm text-muted-foreground">{booking.project_name || "Unknown project"} • {booking.company_name || "Unknown company"}</div>
                          <div className="text-xs text-muted-foreground">Partner: {booking.tenant_name || booking.tenant_id}</div>
                        </div>
                        <Badge variant="outline" className="capitalize">{booking.status}</Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {booking.request_start_date ? new Date(booking.request_start_date).toLocaleString() : ""}
                        {booking.request_end_date ? ` → ${new Date(booking.request_end_date).toLocaleString()}` : ""}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => updateBooking(booking.id, "accepted")}>Accept</Button>
                        <Button size="sm" variant="outline" onClick={() => updateBooking(booking.id, "declined")}>Decline</Button>
                        <Button size="sm" variant="outline" onClick={() => updateBooking(booking.id, "countered")}>Counter</Button>
                      </div>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No direct booking requests right now.</p>}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="messages" className="space-y-6">
          <PortalInbox />
        </TabsContent>

        <TabsContent value="partners" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Partners</CardTitle>
              <CardDescription>Companies and tenant subscribers this subcontractor can work with.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {memberships.length > 0 ? memberships.map((membership) => (
                <div key={membership.membership_id} className="border rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium">{membership.company_name || "Unknown company"}</div>
                      <div className="text-sm text-muted-foreground">{membership.tenant_name || membership.tenant_id}</div>
                    </div>
                    <Badge variant="outline" className="capitalize">{membership.membership_status}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {membership.role_title || "No role title"} • {membership.worker_type || "worker"}
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No active partner connections yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="marketplace" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Briefcase className="h-4 w-4" /> Open Marketplace</CardTitle>
              <CardDescription>Published job opportunities you can claim or express interest in. This is separate from direct booking requests.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded-lg bg-muted/30 p-3">
                <Input placeholder="Search opportunities" value={marketplaceFilters.search} onChange={(e) => setMarketplaceFilters((current) => ({ ...current, search: e.target.value }))} />
                <Input placeholder="Trade" value={marketplaceFilters.trade} onChange={(e) => setMarketplaceFilters((current) => ({ ...current, trade: e.target.value }))} />
                <Input placeholder="State" value={marketplaceFilters.state} onChange={(e) => setMarketplaceFilters((current) => ({ ...current, state: e.target.value }))} />
                <Button variant="outline" onClick={fetchData}>Apply Filters</Button>
              </div>
              {marketplacePosts.length > 0 ? marketplacePosts.map((post) => {
                const form = interestForms[post.id] || {};
                const interest = post.my_interest;
                return (
                  <div key={post.id} className="border rounded-xl p-4 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-semibold">{post.title}</div>
                        <div className="text-sm text-muted-foreground">{post.trade || "General"}{post.csi_code ? ` • ${post.csi_code}` : ""}</div>
                        <div className="text-xs text-muted-foreground">{post.location_text || [post.city, post.state].filter(Boolean).join(", ") || "Location not set"}</div>
                      </div>
                      <Badge variant="outline" className="capitalize">{interest ? interest.status.replaceAll("_", " ") : post.status}</Badge>
                    </div>
                    {post.description && <p className="text-sm text-muted-foreground">{post.description}</p>}
                    {post.scope_of_work && <p className="text-sm">{post.scope_of_work}</p>}
                    <div className="text-xs text-muted-foreground">
                      Deadline: {post.response_deadline ? new Date(post.response_deadline).toLocaleString() : "Not set"}
                      {post.estimated_value_min || post.estimated_value_max ? ` • Est. ${post.estimated_value_min ? `$${Number(post.estimated_value_min).toLocaleString()}` : "?"} - ${post.estimated_value_max ? `$${Number(post.estimated_value_max).toLocaleString()}` : "?"}` : ""}
                    </div>
                    {interest && interest.status !== "withdrawn" ? (
                      <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 p-3">
                        <div className="text-sm">You submitted interest in this opportunity.</div>
                        <Button size="sm" variant="outline" onClick={() => withdrawMarketplaceInterest(interest.id)}>Withdraw</Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg bg-muted/30 p-3">
                        <div className="space-y-2 md:col-span-2"><Label>Message</Label><Textarea value={form.message || ""} onChange={(e) => setInterestForms((current) => ({ ...current, [post.id]: { ...form, message: e.target.value } }))} placeholder="Tell the GC why you're a fit..." /></div>
                        <div className="space-y-2"><Label>Available Start</Label><Input type="datetime-local" value={form.availability_start || ""} onChange={(e) => setInterestForms((current) => ({ ...current, [post.id]: { ...form, availability_start: e.target.value } }))} /></div>
                        <div className="space-y-2"><Label>Available End</Label><Input type="datetime-local" value={form.availability_end || ""} onChange={(e) => setInterestForms((current) => ({ ...current, [post.id]: { ...form, availability_end: e.target.value } }))} /></div>
                        <div className="space-y-2"><Label>Proposed Amount</Label><Input type="number" value={form.proposed_amount || ""} onChange={(e) => setInterestForms((current) => ({ ...current, [post.id]: { ...form, proposed_amount: e.target.value } }))} /></div>
                        <div className="flex items-end"><Button onClick={() => submitMarketplaceInterest(post.id)} className="w-full">Express Interest</Button></div>
                      </div>
                    )}
                  </div>
                );
              }) : <p className="text-sm text-muted-foreground">No open marketplace opportunities right now.</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>My Marketplace Interests</CardTitle>
              <CardDescription>Track opportunities your company has claimed or responded to.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {myMarketplaceInterests.length > 0 ? myMarketplaceInterests.map((interest) => (
                <div key={interest.id} className="border rounded-xl p-4 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{interest.opportunity?.title || "Marketplace opportunity"}</div>
                    <div className="text-sm text-muted-foreground">{interest.opportunity?.trade || "General"} • Submitted {interest.submitted_at ? new Date(interest.submitted_at).toLocaleString() : ""}</div>
                  </div>
                  <Badge variant="outline" className="capitalize">{interest.status.replaceAll("_", " ")}</Badge>
                </div>
              )) : <p className="text-sm text-muted-foreground">You have not submitted marketplace interest yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Briefcase className="h-4 w-4" /> Committed Projects</CardTitle>
              <CardDescription>Assignments and active work you’re currently committed to.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {committedProjects.length > 0 ? committedProjects.map((item) => (
                <div key={item.id} className="border rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium">{item.project_name || "Unknown project"}</div>
                      <div className="text-sm text-muted-foreground">{item.assignment_role || "Assignment"}</div>
                      <div className="text-xs text-muted-foreground">Partner: {item.tenant_name || item.tenant_id}</div>
                    </div>
                    <Badge variant="outline" className="capitalize">{item.status}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground mt-2">Planned hours: {item.hours_planned}</div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No committed projects yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="opportunities" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Opportunities</CardTitle>
              <CardDescription>Open or countered booking requests you can still respond to.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {opportunities.length > 0 ? opportunities.map((booking) => (
                <div key={booking.id} className="border rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-semibold">{booking.title}</div>
                      <div className="text-sm text-muted-foreground">{booking.project_name || "Unknown project"} • {booking.company_name || "Unknown company"}</div>
                      <div className="text-xs text-muted-foreground">Partner: {booking.tenant_name || booking.tenant_id}</div>
                    </div>
                    <Badge variant="outline" className="capitalize">{booking.status}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateBooking(booking.id, "accepted")}>Accept</Button>
                    <Button size="sm" variant="outline" onClick={() => updateBooking(booking.id, "declined")}>Decline</Button>
                    <Button size="sm" variant="outline" onClick={() => updateBooking(booking.id, "countered")}>Counter</Button>
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No open opportunities right now.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><SettingsIcon className="h-4 w-4" /> Settings</CardTitle>
              <CardDescription>Update your profile details and portal password.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSettingsSubmit} className="space-y-4 max-w-xl">
                <div className="space-y-2"><Label>Full Name</Label><Input value={settingsForm.full_name} onChange={(e) => setSettingsForm({ ...settingsForm, full_name: e.target.value })} className="bg-muted/50" /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={settingsForm.phone} onChange={(e) => setSettingsForm({ ...settingsForm, phone: e.target.value })} className="bg-muted/50" /></div>
                <div className="space-y-2"><Label>New Password</Label><Input type="password" value={settingsForm.password} onChange={(e) => setSettingsForm({ ...settingsForm, password: e.target.value })} className="bg-muted/50" placeholder="Leave blank to keep current password" /></div>
                <Button type="submit" className="bg-primary hover:bg-primary/90">Save Settings</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PortalHomePage;
